import type { MediaObject } from 'src/api/services/MediaClient/MediaClient.types';
import { MediaScanHold, scanHoldLabel } from 'src/api/services/MediaClient/media-scan-hold';

/**
 * Every uploaded file is checked for viruses before anyone gets its bytes.
 * While it is held the API sends no links to it (index_url null, no
 * conversions, no video) and the URL endpoints answer 409 with `scan_held`.
 *
 * Holds are keyed on the server's `scan_held` only, never re-derived from
 * `scan_status`, which just picks the label (see media-scan-hold.ts).
 */
export type { MediaScanHold };

type ScanFields = Pick<MediaObject, 'scan_held' | 'scan_status'>;

export const isScanHeld = (media?: ScanFields | null): boolean => media?.scan_held === true;

export const getScanHold = (media?: ScanFields | null): MediaScanHold | null =>
  isScanHeld(media) ? scanHoldLabel(media?.scan_status) : null;

/** Held and still being scanned: the only hold that clears on its own, so worth polling for. */
export const isScanPending = (media?: ScanFields | null): boolean =>
  isScanHeld(media) && media?.scan_status === 'pending';

/** Released, but without a finished scan (the scanner was unavailable). */
export const isReleasedUnscanned = (media?: ScanFields | null): boolean =>
  !isScanHeld(media) && media?.scan_status === 'released_unscanned';

type ScanHeldError = { scanHeld: true; scanStatus?: string; message: string };

/** A 409 a URL endpoint returns for a file held by the scan (see HttpError). */
export const isScanHeldError = (error: unknown): error is ScanHeldError =>
  !!error && typeof error === 'object' && (error as { scanHeld?: unknown }).scanHeld === true;

export const scanHoldFromError = (error: unknown): MediaScanHold | null =>
  isScanHeldError(error) ? scanHoldLabel(error.scanStatus) : null;

/** Distinct held files among `media` (the same file can appear in several lists). */
export const countScanHeld = (
  media: ReadonlyArray<(ScanFields & Pick<MediaObject, 'id'>) | null | undefined>,
): number => new Set(media.filter(isScanHeld).map((m) => m!.id)).size;

const MAX_PAYLOAD_DEPTH = 6;

/**
 * Whether anything in an API payload is a media object that's still being
 * scanned. Walks objects and arrays generically (bounded depth) so every place
 * media can sit (raw footage, revisions, thumbnails, comment attachments) is
 * covered without listing them.
 */
export const payloadHasScanPending = (value: unknown, depth = 0): boolean => {
  if (!value || typeof value !== 'object' || depth > MAX_PAYLOAD_DEPTH) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => payloadHasScanPending(item, depth + 1));
  }
  if ('scan_held' in value && isScanPending(value as ScanFields)) {
    return true;
  }
  return Object.values(value).some((item) => payloadHasScanPending(item, depth + 1));
};
